import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { TOUR_PANELS, GALLERY_KEYS, GALLERY, TOUR, guideTo, guideLabel } from '../../../content/landingTour';
import { PANELS } from './panels';
import NearViewport from './NearViewport';
import { Eyebrow, Arrow } from './parts';

// "See it in the app": six real product screens below the planning section
// (founder decision A, 2026-09-29, ADR 0022). The screens are the replicas the
// old feature rows used, each shown as a window onto the top of the screen
// with one plain line and its free guide; the whole screen, and the rest, are
// on /tour. Nothing here loads until the section nears the screen (a chunk per
// panel), so the first screen pays for none of it.

const PREVIEW_H = 'h-[360px] sm:h-[400px]';

/** A panel's link: its own page, else its guide, else none. */
export function guideHref(panel) {
    if (panel.to) return panel.to;
    if (!panel.paper) return null;
    return guideTo(panel);
}

function GalleryCard({ panel, href }) {
    const Panel = PANELS[panel.key];
    return (
        <li className="flex min-w-0 flex-col rounded-2xl border border-[#E0DED8] bg-white p-4 transition-colors hover:border-[#1A4D2E]/40 sm:p-5" data-testid={`landing-gallery-${panel.key}`}>
            <Eyebrow>{panel.eyebrow}</Eyebrow>
            <h3 className="[text-wrap:balance] mt-1 font-serif text-xl font-bold text-[#1A4D2E]">{panel.title}</h3>
            <p className="mt-1 text-base leading-snug text-[#4A4A4A] [text-wrap:pretty] sm:min-h-[3rem]">{panel.line}</p>
            {/* A window onto the top of the real screen: the replica is not
                interactive here (its buttons are pictures of buttons), and the
                line above says what it shows, so it is hidden from screen
                readers rather than read twice. */}
            <div className={`relative mt-3 overflow-hidden rounded-xl border border-[#EDEBE5] bg-[#FDFCF7] ${PREVIEW_H}`} aria-hidden="true" data-replica>
                {/* Drawn at 85% so a phone-width card shows the screen's full
                    width, the way a screenshot would. */}
                <div className="pointer-events-none origin-top-left p-3" style={{ width: '117.6%', transform: 'scale(0.85)' }}>
                    <Suspense fallback={<div className={`${PREVIEW_H} rounded-lg bg-[#F5F3EE]`} />}>
                        <Panel />
                    </Suspense>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
            </div>
            {href && (
                <p className="mt-auto pt-3">
                    <Link to={href} data-cta={panel.cta} className="inline-block py-2 text-base font-semibold text-[#1A4D2E] hover:underline">
                        {guideLabel(panel)}<span className="ml-1.5 inline-block align-[-0.15em]"><Arrow /></span>
                    </Link>
                </p>
            )}
        </li>
    );
}

export default function Gallery() {
    const panels = GALLERY_KEYS
        .map((k) => TOUR_PANELS.find((p) => p.key === k))
        .filter(Boolean);
    return (
        <section id="gallery" data-landing-section="gallery" data-testid="landing-gallery" aria-labelledby="landing-gallery-title"
                 className="scroll-mt-20 bg-white px-4 py-10 sm:px-6 lg:px-8 lg:py-20">
            <div className="mx-auto max-w-6xl">
                <div className="mx-auto mb-8 max-w-3xl text-center">
                    <Eyebrow>In the app</Eyebrow>
                    <h2 id="landing-gallery-title" className="[text-wrap:balance] mb-3 font-serif text-3xl font-bold leading-tight text-[#1A4D2E] sm:text-4xl">{GALLERY.title}</h2>
                    <p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#4A4A4A]">{GALLERY.sub}</p>
                </div>
                <NearViewport minHeight={900}>
                    <ul role="list" className="m-0 grid list-none gap-4 p-0 sm:grid-cols-2 lg:gap-6">
                        {panels.map((p) => <GalleryCard key={p.key} panel={p} href={guideHref(p)} />)}
                    </ul>
                </NearViewport>
                <p className="mt-6 text-center">
                    <Link to={TOUR.path} data-cta="tour_link" className="inline-flex min-h-[44px] items-center gap-1.5 text-base font-semibold text-[#1A4D2E] hover:underline">
                        {GALLERY.tourLink} <Arrow />
                    </Link>
                </p>
            </div>
        </section>
    );
}
